/**
 * useNotificationsStore — Pinia replacement for the legacy Vuex `notifications/`
 * module.
 *
 * Phase 3 of the Vuex → Pinia migration (architecture review §5).  Holds the
 * persistent notification feed for the authenticated user.  Public surface
 * mirrors the old module so callers migrate one-to-one:
 *
 *   Old:                                              New:
 *   legacy unread-count state                         notificationsStore.unreadCount
 *   store.getters['notifications/items']              notificationsStore.items
 *   legacy refresh dispatch                           notificationsStore.refresh()
 *
 * Mutations from the old module collapse into direct state mutation inside
 * actions — Pinia doesn't require the mutation indirection.  Optimistic
 * updates (mark-as-read, delete) still roll back on API error.
 */

import { getApiBaseUrl } from '@/app/runtime';
import i18n from '@/i18n';
import { showToast } from '@/plugins/toast';
import NotificationsRepository from '@/features/notifications/api/NotificationsRepository';
import { usePollingController } from '@/composables/usePollingController';
import normalizeBaseDomain from '@/repositories/clients/normalizeBaseDomain';
import { defineStore } from 'pinia';

function t(key) {
  return i18n.global.t(key);
}

const POLL_INTERVAL_MS = 30000;
const SSE_RETRY_BASE_MS = 2000;
const SSE_RETRY_MAX_MS = 60000;

// Singleton poll controller for the unread-count tick. Held at module scope (the
// store is a singleton) so it survives across start/stop calls. Constant 30s
// cadence, but pauses entirely while the tab is hidden and refreshes on return.
// A recent-ledger fetch younger than this is reused by `start()`.
const RECENT_FRESH_MS = 10_000;

let unreadPoller = null;

// SSE connection state (module-level; store is a singleton).
let sseSource = null;
let sseRetryTimer = null;
let sseRetryDelay = SSE_RETRY_BASE_MS;

const DEFAULT_FILTERS = {
  status: 'all',
  notification_type: null,
  since: null,
  until: null,
};

function toStatus(doc) {
  if (!doc) return 'unread';
  if (doc.status) return doc.status;
  if (doc.read_at) return 'read';
  return 'unread';
}

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    byId: {},
    ids: [],
    // Recent-ledger slice for the bell dropdown — kept SEPARATE from the
    // paginated `byId`/`ids` (the full page) so page filters never pollute the
    // dropdown and vice-versa. Sourced from GET /notifications/recent (Redis).
    recentById: {},
    recentIds: [],
    recentLoading: false,
    recentFetchedAt: 0,
    unreadCount: 0,
    filters: { ...DEFAULT_FILTERS },
    pagination: { nextCursor: null, loading: false, exhausted: false },
    pollTimerId: null,
  }),

  getters: {
    items: (state) => state.ids.map((id) => state.byId[id]).filter(Boolean),
    recentItems: (state) => state.recentIds.map((id) => state.recentById[id]).filter(Boolean),
    unreadItems() {
      return this.items.filter((n) => toStatus(n) === 'unread');
    },
    hasUnread: (state) => state.unreadCount > 0,
  },

  actions: {
    _setNotifications({ items, replace }) {
      if (replace) {
        this.byId = {};
        this.ids = [];
      }
      const incoming = [];
      for (const doc of items) {
        if (!doc || !doc.id) continue;
        this.byId = { ...this.byId, [doc.id]: { ...doc, status: toStatus(doc) } };
        if (!this.ids.includes(doc.id)) incoming.push(doc.id);
      }
      if (incoming.length) this.ids = [...this.ids, ...incoming];
    },

    _prependNotification(doc) {
      if (!doc || !doc.id) return;
      const normalised = { ...doc, status: toStatus(doc) };
      this.byId = { ...this.byId, [doc.id]: normalised };
      if (!this.ids.includes(doc.id)) {
        this.ids = [doc.id, ...this.ids];
      }
    },

    _updateNotification(id, patch) {
      const existing = this.byId[id];
      if (!existing) return;
      const merged = { ...existing, ...patch };
      merged.status = toStatus(merged);
      this.byId = { ...this.byId, [id]: merged };
    },

    _removeNotification(id) {
      if (!(id in this.byId)) return;
      const { [id]: _removed, ...rest } = this.byId;
      this.byId = rest;
      this.ids = this.ids.filter((x) => x !== id);
    },

    _setUnreadCount(n) {
      this.unreadCount = Math.max(0, Number(n) || 0);
    },

    _setRecent(items) {
      const nextById = {};
      const nextIds = [];
      for (const doc of items || []) {
        if (!doc || !doc.id) continue;
        nextById[doc.id] = { ...doc, status: toStatus(doc) };
        if (!nextIds.includes(doc.id)) nextIds.push(doc.id);
      }
      this.recentById = nextById;
      this.recentIds = nextIds;
    },

    _patchRecent(id, patch) {
      const existing = this.recentById[id];
      if (!existing) return;
      const merged = { ...existing, ...patch };
      merged.status = toStatus(merged);
      this.recentById = { ...this.recentById, [id]: merged };
    },

    _removeRecent(id) {
      if (!(id in this.recentById)) return;
      const { [id]: _removed, ...rest } = this.recentById;
      this.recentById = rest;
      this.recentIds = this.recentIds.filter((x) => x !== id);
    },

    /**
     * Refresh the bell dropdown from the Redis recent-ledger. One call updates
     * BOTH the dropdown list (recent slice) and the unread badge, and is immune
     * to Elasticsearch refresh lag on freshly-created notifications.
     */
    async fetchRecent() {
      // Only surface a loading state on the very first load (empty list), so
      // background poll ticks never flash the dropdown into a skeleton.
      if (this.recentIds.length === 0) this.recentLoading = true;
      try {
        const { data } = await NotificationsRepository.recent();
        this._setRecent(data?.items || []);
        this._setUnreadCount(data?.unread_count ?? this.unreadCount);
        this.recentFetchedAt = Date.now();
      } catch (_err) {
        // Best-effort: dropdown keeps its last-known items; next tick reconciles.
      } finally {
        this.recentLoading = false;
      }
    },

    async fetchPage({ reset = false } = {}) {
      if (this.pagination.loading) return;
      this.pagination = { ...this.pagination, loading: true };
      try {
        const params = { ...this.filters, limit: 25 };
        if (!reset && this.pagination.nextCursor) {
          params.cursor = this.pagination.nextCursor;
        }
        const { data } = await NotificationsRepository.list(params);
        this._setNotifications({ items: data?.items || [], replace: reset });
        this._setUnreadCount(data?.unread_count ?? this.unreadCount);
        this.pagination = {
          ...this.pagination,
          nextCursor: data?.next_cursor || null,
          exhausted: !data?.next_cursor,
        };
        // Sync bell dropdown so it reflects the same items (non-blocking).
        if (reset) this.fetchRecent();
      } catch (_err) {
        // Best-effort: list calls are non-critical.
      } finally {
        this.pagination = { ...this.pagination, loading: false };
      }
    },

    async markRead(id) {
      const before = this.byId[id] || this.recentById[id];
      if (!before || before.read_at) return;
      const now = new Date().toISOString();
      this._updateNotification(id, { read_at: now });
      this._patchRecent(id, { read_at: now });
      this._setUnreadCount(this.unreadCount - 1);
      try {
        const { data } = await NotificationsRepository.markRead(id);
        if (data?.unread_count != null) this._setUnreadCount(data.unread_count);
        showToast({ text: t('messages.notification_marked_read'), tone: 'success' });
      } catch (err) {
        this._updateNotification(id, { read_at: before.read_at || null });
        this._patchRecent(id, { read_at: before.read_at || null });
        this._setUnreadCount(this.unreadCount + 1);
        showToast({ text: t('messages.notification_action_failed'), tone: 'danger' });
        throw err;
      }
    },

    async markAllRead() {
      const previousUnreadIds = this.unreadItems.map((n) => n.id);
      const previousUnreadCount = this.unreadCount;
      const now = new Date().toISOString();
      const previousRecentUnread = this.recentItems
        .filter((n) => toStatus(n) === 'unread')
        .map((n) => n.id);
      for (const id of previousUnreadIds) {
        this._updateNotification(id, { read_at: now });
      }
      for (const id of previousRecentUnread) {
        this._patchRecent(id, { read_at: now });
      }
      this._setUnreadCount(0);
      try {
        const { data } = await NotificationsRepository.markAllRead();
        if (data?.unread_count != null) this._setUnreadCount(data.unread_count);
        showToast({ text: t('messages.notifications_marked_read'), tone: 'success' });
      } catch (err) {
        for (const id of previousUnreadIds) {
          this._updateNotification(id, { read_at: null });
        }
        for (const id of previousRecentUnread) {
          this._patchRecent(id, { read_at: null });
        }
        this._setUnreadCount(previousUnreadCount);
        showToast({ text: t('messages.notification_action_failed'), tone: 'danger' });
        throw err;
      }
    },

    async deleteNotification(id) {
      const before = this.byId[id] || this.recentById[id];
      if (!before) return;
      const wasUnread = !before.read_at;
      const wasInRecent = id in this.recentById;
      this._removeNotification(id);
      this._removeRecent(id);
      if (wasUnread) this._setUnreadCount(this.unreadCount - 1);
      try {
        await NotificationsRepository.delete(id);
        await this.fetchRecent();
        showToast({ text: t('messages.notification_deleted'), tone: 'success' });
      } catch (err) {
        this._prependNotification(before);
        if (wasInRecent) this._setRecent([before, ...this.recentItems]);
        if (wasUnread) this._setUnreadCount(this.unreadCount + 1);
        showToast({ text: t('messages.notification_action_failed'), tone: 'danger' });
        throw err;
      }
    },

    setFilters(patch) {
      const { severity: _severity, ...rest } = patch || {};
      const next = { ...this.filters, ...rest };
      delete next.severity;
      this.filters = next;
      this.pagination = { ...this.pagination, nextCursor: null, exhausted: false };
      return this.fetchPage({ reset: true });
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.pagination = { ...this.pagination, nextCursor: null, exhausted: false };
      return this.fetchPage({ reset: true });
    },

    startRealtime() {
      if (sseSource) return; // Already connected.
      const token = sessionStorage.getItem('accessToken');
      if (!token) {
        // Fall back to polling when no token is available (e.g. during auth flows).
        this.startPolling();
        return;
      }

      const connect = () => {
        if (sseSource) return;
        // Same API base as the axios clients: a relative URL hits the portal
        // nginx, whose SPA fallback answers text/html and EventSource aborts.
        const base = normalizeBaseDomain(getApiBaseUrl());
        const url = `${base}/notifications/stream?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        sseSource = es;

        es.addEventListener('created', () => {
          sseRetryDelay = SSE_RETRY_BASE_MS; // Reset backoff on successful message.
          this.fetchRecent();
        });

        es.addEventListener('read', () => {
          sseRetryDelay = SSE_RETRY_BASE_MS;
          this.fetchRecent();
        });

        es.addEventListener('deleted', () => {
          sseRetryDelay = SSE_RETRY_BASE_MS;
          this.fetchRecent();
        });

        es.onopen = () => {
          sseRetryDelay = SSE_RETRY_BASE_MS;
          // Stop the fallback poller once SSE is live.
          unreadPoller?.stop();
        };

        es.onerror = () => {
          es.close();
          sseSource = null;
          // Exponential backoff, capped at SSE_RETRY_MAX_MS.
          sseRetryTimer = setTimeout(() => {
            sseRetryTimer = null;
            connect();
          }, sseRetryDelay);
          sseRetryDelay = Math.min(sseRetryDelay * 2, SSE_RETRY_MAX_MS);
        };
      };

      connect();
    },

    stopRealtime() {
      if (sseRetryTimer) {
        clearTimeout(sseRetryTimer);
        sseRetryTimer = null;
      }
      if (sseSource) {
        sseSource.close();
        sseSource = null;
      }
      sseRetryDelay = SSE_RETRY_BASE_MS;
    },

    startPolling() {
      if (!unreadPoller) {
        // Polls the recent-ledger endpoint: one tick refreshes BOTH the bell
        // badge and the dropdown list (and is immune to ES refresh lag).
        unreadPoller = usePollingController(() => this.fetchRecent(), {
          baseMs: POLL_INTERVAL_MS,
          maxMs: POLL_INTERVAL_MS,
        });
      }
      // start() already does the initial fetch, so skip the immediate tick.
      unreadPoller.start({ immediate: false });
    },

    stopPolling() {
      unreadPoller?.stop();
    },

    /**
     * Refresh the in-app feed AND unread counter from the backend.
     * Called by the service-worker bridge in App.vue on a web-push arrival.
     */
    async refresh() {
      // The bell (badge + dropdown) is driven by the Redis recent-ledger, which
      // is authoritative and ES-consistency-proof — so fetchRecent() is all the
      // bell needs. Also refresh the full-page slice when it's already populated
      // so an open /notifications view stays live.
      this.pagination = { ...this.pagination, nextCursor: null, exhausted: false };
      const tasks = [this.fetchRecent()];
      if (this.ids.length > 0) tasks.push(this.fetchPage({ reset: true }));
      await Promise.all(tasks);
    },

    start() {
      // The Home notifications card already loads the ledger on mount; don't
      // repeat the same request right after it.
      if (Date.now() - this.recentFetchedAt > RECENT_FRESH_MS) this.fetchRecent();
      this.startRealtime();
    },

    stop() {
      this.stopRealtime();
      this.stopPolling();
    },
  },
});
